(function () {
  var surface = null;
  var stops = [];
  var ticking = false;
  var DEFAULT_TRANSITION_START = 0.72;
  var targets = {};
  var headerSchemeOverrides = {};
  var headerOverrideOrder = 0;

  var HEADER_SCHEMES = {
    dark: {
      foreground: { r: 17, g: 17, b: 17 },
      invert: "0.000",
    },
    light: {
      foreground: { r: 244, g: 244, b: 242 },
      invert: "1.000",
    },
  };

  if (
    document.body &&
    document.body.classList.contains("template-product") &&
    document.querySelector("[data-cd-pdp-surface-start]")
  ) {
    document.documentElement.classList.add("cd-pdp-surface-ready");
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function smoothstep(value) {
    var t = clamp(value, 0, 1);
    return t * t * (3 - 2 * t);
  }

  function parseColor(input) {
    if (!input) return null;

    var value = String(input).trim();
    var hex = value.replace(/^#/, "");

    if (/^[0-9a-fA-F]{3}$/.test(hex)) {
      return {
        r: parseInt(hex.charAt(0) + hex.charAt(0), 16),
        g: parseInt(hex.charAt(1) + hex.charAt(1), 16),
        b: parseInt(hex.charAt(2) + hex.charAt(2), 16),
      };
    }

    if (/^[0-9a-fA-F]{6}$/.test(hex)) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }

    var rgbMatch = value.match(/rgba?\(([^)]+)\)/i);
    if (rgbMatch) {
      var channels = rgbMatch[1].split(",").map(function (channel) {
        return parseFloat(channel.trim());
      });

      if (
        channels.length >= 3 &&
        channels.every(function (channel) {
          return !Number.isNaN(channel);
        })
      ) {
        return {
          r: clamp(Math.round(channels[0]), 0, 255),
          g: clamp(Math.round(channels[1]), 0, 255),
          b: clamp(Math.round(channels[2]), 0, 255),
        };
      }
    }

    return null;
  }

  function mixColor(from, to, progress) {
    return {
      r: Math.round(from.r + (to.r - from.r) * progress),
      g: Math.round(from.g + (to.g - from.g) * progress),
      b: Math.round(from.b + (to.b - from.b) * progress),
    };
  }

  function colorToCss(color) {
    return "rgb(" + color.r + ", " + color.g + ", " + color.b + ")";
  }

  function setHeaderSchemeOverride(source, scheme) {
    var key = source || "default";

    if (!scheme || !HEADER_SCHEMES[scheme]) {
      delete headerSchemeOverrides[key];
    } else {
      headerSchemeOverrides[key] = {
        scheme: scheme,
        order: ++headerOverrideOrder,
      };
    }

    var active = getActiveHeaderSchemeOverride();
    if (active) {
      document.documentElement.setAttribute(
        "data-cd-pdp-header-force-scheme",
        active.scheme
      );
    } else {
      document.documentElement.removeAttribute("data-cd-pdp-header-force-scheme");
    }

    queueUpdate();
  }

  function getActiveHeaderSchemeOverride() {
    var active = null;

    Object.keys(headerSchemeOverrides).forEach(function (key) {
      var item = headerSchemeOverrides[key];
      if (!item || !HEADER_SCHEMES[item.scheme]) return;
      if (!active || item.order >= active.order) active = item;
    });

    var attr = document.documentElement.getAttribute(
      "data-cd-pdp-header-force-scheme"
    );
    if (attr && HEADER_SCHEMES[attr] && !active) {
      active = { scheme: attr, order: 0 };
    }

    return active;
  }

  function getHeaderForegroundState(foregroundCss, foregroundRgb, invertAmount) {
    var active = getActiveHeaderSchemeOverride();
    if (!active || !HEADER_SCHEMES[active.scheme]) {
      return {
        foregroundCss: foregroundCss,
        foregroundRgb: foregroundRgb,
        invertAmount: invertAmount,
        scheme: null,
      };
    }

    var scheme = HEADER_SCHEMES[active.scheme];
    var rgb =
      scheme.foreground.r + ", " + scheme.foreground.g + ", " + scheme.foreground.b;

    return {
      foregroundCss: colorToCss(scheme.foreground),
      foregroundRgb: rgb,
      invertAmount: scheme.invert,
      scheme: active.scheme,
    };
  }

  function channelToLinear(channel) {
    var value = channel / 255;
    return value <= 0.04045
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4);
  }

  function getLuminance(color) {
    var r = channelToLinear(color.r);
    var g = channelToLinear(color.g);
    var b = channelToLinear(color.b);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  function standardizeSurfaceColor(color) {
    if (!color) return null;

    return getLuminance(color) < 0.35
      ? { r: 21, g: 21, b: 21 }
      : { r: 255, g: 255, b: 255 };
  }

  function getForegroundBlend(color) {
    var luminance = getLuminance(color);
    return smoothstep((0.68 - luminance) / 0.42);
  }

  function getForegroundColor(color) {
    return mixColor(
      { r: 17, g: 17, b: 17 },
      { r: 244, g: 244, b: 242 },
      getForegroundBlend(color),
    );
  }

  function getBinaryForegroundColor(color) {
    return getLuminance(color) > 0.46
      ? { r: 17, g: 17, b: 17 }
      : { r: 244, g: 244, b: 242 };
  }

  function getTransitionStartRatio(node) {
    var raw = parseFloat(node.getAttribute("data-cd-pdp-surface-transition-start"));
    if (!Number.isNaN(raw)) return clamp(raw, 0, 0.98);
    return DEFAULT_TRANSITION_START;
  }

  function getTransitionStartY(stop) {
    var range = Math.max(stop.bottom - stop.top, 1);
    var viewport = Math.max(window.innerHeight || 0, 1);
    var minWindow = Math.min(Math.max(viewport * 0.2, 140), range * 0.45);
    var start = stop.top + range * stop.transitionStartRatio;
    return Math.min(start, stop.bottom - minWindow);
  }

  function readStops() {
    var nodes = Array.prototype.slice.call(
      document.querySelectorAll("[data-cd-pdp-surface-start]"),
    );

    stops = nodes
      .map(function (node) {
        var start = standardizeSurfaceColor(
          parseColor(node.getAttribute("data-cd-pdp-surface-start")),
        );
        if (!start) return null;

        return {
          node: node,
          start: start,
          explicitEnd: standardizeSurfaceColor(
            parseColor(node.getAttribute("data-cd-pdp-surface-end")),
          ),
          end: start,
          transitionStartRatio: getTransitionStartRatio(node),
          top: 0,
          bottom: 0,
        };
      })
      .filter(Boolean);

    stops.forEach(function (stop, index) {
      var next = stops[index + 1];
      stop.end = stop.explicitEnd || (next ? next.start : stop.start);
    });
  }

  function measureStops() {
    stops.forEach(function (stop) {
      var rect = stop.node.getBoundingClientRect();
      var top = rect.top + window.scrollY;
      var height = Math.max(rect.height, stop.node.offsetHeight || 0, 1);
      stop.top = top;
      stop.bottom = top + height;
    });
  }

  function findColor(anchor) {
    if (!stops.length) return null;

    if (anchor <= stops[0].top) {
      return stops[0].start;
    }

    for (var index = 0; index < stops.length; index += 1) {
      var stop = stops[index];

      if (anchor >= stop.top && anchor <= stop.bottom) {
        var transitionStart = getTransitionStartY(stop);
        var progress =
          (anchor - transitionStart) /
          Math.max(stop.bottom - transitionStart, 1);
        return mixColor(stop.start, stop.end, smoothstep(progress));
      }

      var next = stops[index + 1];
      if (next && anchor > stop.bottom && anchor < next.top) {
        var gapProgress =
          (anchor - stop.bottom) / Math.max(next.top - stop.bottom, 1);
        return mixColor(stop.end, next.start, smoothstep(gapProgress));
      }
    }

    return stops[stops.length - 1].end;
  }

  function collectNodes(selector, filter) {
    return Array.prototype.slice
      .call(document.querySelectorAll(selector))
      .filter(function (node) {
        return !filter || filter(node);
      });
  }

  function isMorphableTextNode(node) {
    if (!node) return false;
    if (node.closest('.cd-tb-hero')) return false;

    // The Technical Specifications heading follows the changing PDP surface,
    // but chip and card content must retain its own fixed high-contrast color.
    if (node.closest('.cd-home-specs__item')) return false;

    if (
      node.closest(
        'button, [role="button"], .button, .btn, .cd-pdp-hero__button, .cd-home-footer__subscribe, input, textarea, select, option, summary, svg',
      )
    ) {
      return false;
    }
    return true;
  }

  function collectTargets() {
    var textSelector =
      '#MainContent .shopify-section :is(h1, h2, h3, h4, h5, h6, p, span, a, small, strong, em, li, dt, dd, label, blockquote, cite, [class*="title"], [class*="heading"], [class*="copy"], [class*="body"], [class*="caption"], [class*="eyebrow"], [class*="description"], [class*="content"], [class*="price"], [class*="rating"], [class*="meta"], [class*="side"], [class*="subheading"], [class*="subtitle"], [class*="text"], [class*="question"], [class*="answer"])';

    targets = {
      header: document.querySelector("[data-cd-hero-header]"),
      headerLogos: collectNodes(
        "[data-cd-hero-header] .cd-hero-header__logo img, [data-cd-hero-header] .cd-hero-header__mobile-logo img",
      ),
      headerText: collectNodes(
        "[data-cd-hero-header] .cd-hero-header__nav-link, [data-cd-hero-header] .cd-hero-header__cart-link, [data-cd-hero-header] .cd-hero-header__menu-trigger, [data-cd-hero-header] .t4s-push-menu-btn",
      ),
      headerIcons: collectNodes(
        '[data-cd-hero-header] svg, [data-cd-hero-header] svg *, [data-cd-hero-header] [class*="icon"]',
      ),
      pdpHeroes: collectNodes(".cd-pdp-hero"),
      pdpStatements: collectNodes(".cd-pdp-statement-reveal"),
      pdpProductImages: collectNodes(".cd-pdp-product-images"),
      tbHeroes: collectNodes(".cd-tb-hero"),
      safeSecure: collectNodes(".cd-home-safe-secure"),
      disinfectDry: collectNodes(".cd-hdd"),
      specs: collectNodes(".cd-home-specs"),
      faqs: collectNodes(".cd-home-faq"),
      footers: collectNodes(".cd-home-footer"),
      primaryText: collectNodes(textSelector, isMorphableTextNode),
      mutedText: collectNodes(
        '#MainContent .shopify-section :is([class*="compare"], [class*="muted"], [class*="meta"], [class*="date"], [class*="excerpt"], [class*="caption"], [class*="subtext"], [class*="subtitle"], [class*="eyebrow"], [class*="answer"])',
        isMorphableTextNode,
      ),
      lineNodes: collectNodes(
        ".cd-pdp-product-images__features-row, .cd-pdp-product-images__feature-item, .cd-pdp-product-images__progress, .cd-home-specs__list, .cd-home-specs__item, .cd-home-faq__topline, .cd-home-faq__item, .cd-home-footer__newsletter-input, .cd-home-footer__social-link, .cd-home-footer__menu a, .cd-home-footer__signup-row",
      ),
      footerFields: collectNodes(
        ".cd-home-footer__newsletter-input input, .cd-home-footer__newsletter-input textarea",
      ),
    };
  }

  function setPropertyOnNodes(nodes, property, value) {
    if (!nodes || !nodes.length) return;
    nodes.forEach(function (node) {
      node.style.setProperty(property, value);
    });
  }

  function setInlineColor(nodes, value) {
    if (!nodes || !nodes.length) return;
    nodes.forEach(function (node) {
      if (node) {
        node.style.setProperty("color", value, "important");
      }
    });
  }

  function setInlineBorderColor(nodes, value) {
    if (!nodes || !nodes.length) return;
    nodes.forEach(function (node) {
      if (node) {
        node.style.setProperty("border-color", value, "important");
      }
    });
  }

  function applyForegroundOverrides(
    cssValue,
    foregroundCss,
    foregroundRgb,
    mutedCss,
    lineCss,
    lineStrongCss,
    invertAmount,
    tbForegroundCss,
    tbMutedCss,
    tbLineCss,
    tbLineStrongCss,
  ) {
    // The PDP surface continues to blend the page content, but the shared
    // navigation is deliberately independent: it is always black on opaque
    // white on every template and at every scroll position.
    var headerForegroundCss = "rgb(21, 21, 21)";
    var headerForegroundRgb = "21, 21, 21";
    var headerInvertAmount = "0";

    if (targets.header) {
      targets.header.style.setProperty("color", headerForegroundCss, "important");
      targets.header.style.setProperty("--h-text-color", headerForegroundCss, "important");
      targets.header.style.setProperty("--h-text-color-rgb", headerForegroundRgb, "important");
      targets.header.style.setProperty(
        "--h-text-color-hover",
        "rgba(" + headerForegroundRgb + ", 0.78)",
        "important",
      );
      targets.header.style.setProperty("--h-bg-color", "#ffffff", "important");
      targets.header.style.setProperty("background", "#ffffff", "important");
      targets.header.style.setProperty("background-color", "#ffffff", "important");
      targets.header.removeAttribute("data-cd-pdp-header-forced-scheme");
    }

    setInlineColor(targets.headerText, headerForegroundCss);
    if (targets.headerIcons && targets.headerIcons.length) {
      targets.headerIcons.forEach(function (node) {
        node.style.setProperty("color", headerForegroundCss, "important");
        if (node.tagName && node.tagName.toLowerCase() !== "svg") {
          node.style.setProperty("stroke", "currentColor", "important");
          if (
            node.getAttribute &&
            node.getAttribute("fill") &&
            node.getAttribute("fill") !== "none"
          ) {
            node.style.setProperty("fill", "currentColor", "important");
          }
        }
      });
    }

    if (targets.headerLogos && targets.headerLogos.length) {
      var headerLogoFilter = "invert(" + headerInvertAmount + ") brightness(1.02)";
      targets.headerLogos.forEach(function (node) {
        node.style.setProperty("filter", headerLogoFilter, "important");
      });
    }

    setPropertyOnNodes(targets.pdpHeroes, "--cd-pdp-hero-text", foregroundCss);
    setPropertyOnNodes(targets.pdpHeroes, "--cd-pdp-hero-subtle", mutedCss);
    setInlineColor(targets.pdpHeroes, foregroundCss);

    setPropertyOnNodes(targets.pdpStatements, "--cd-pdp-statement-text", foregroundCss);
    setInlineColor(targets.pdpStatements, foregroundCss);

    setPropertyOnNodes(targets.pdpProductImages, "--cd-pdp-product-text", foregroundCss);
    setPropertyOnNodes(targets.pdpProductImages, "--cd-pdp-product-border", lineStrongCss);
    setPropertyOnNodes(targets.pdpProductImages, "--cd-pdp-product-track", lineCss);
    setPropertyOnNodes(
      targets.pdpProductImages,
      "--cd-pdp-product-progress",
      "rgba(" + foregroundRgb + ", 0.58)",
    );
    setInlineColor(targets.pdpProductImages, foregroundCss);

    setPropertyOnNodes(targets.tbHeroes, "--cd-tb-bg", cssValue);
    setPropertyOnNodes(targets.tbHeroes, "--cd-tb-card-bg", cssValue);
    setPropertyOnNodes(targets.tbHeroes, "--cd-tb-text", tbForegroundCss);
    setPropertyOnNodes(targets.tbHeroes, "--cd-tb-muted", tbMutedCss);
    setPropertyOnNodes(targets.tbHeroes, "--cd-tb-border", tbLineCss);
    setPropertyOnNodes(targets.tbHeroes, "--cd-tb-card-border", tbLineStrongCss);

    setPropertyOnNodes(targets.safeSecure, "--cd-safe-text-dark", foregroundCss);
    setPropertyOnNodes(targets.safeSecure, "--cd-safe-text-light", foregroundCss);
    setInlineColor(targets.safeSecure, foregroundCss);

    setPropertyOnNodes(targets.disinfectDry, "--cd-hdd-text", foregroundCss);
    setInlineColor(targets.disinfectDry, foregroundCss);

    setPropertyOnNodes(targets.specs, "--cd-home-specs-heading", foregroundCss);
    setPropertyOnNodes(targets.specs, "--cd-home-specs-copy", mutedCss);
    setPropertyOnNodes(targets.specs, "--cd-home-specs-rule", lineStrongCss);
    setInlineColor(targets.specs, foregroundCss);

    setPropertyOnNodes(targets.faqs, "--cd-home-faq-heading", foregroundCss);
    setPropertyOnNodes(targets.faqs, "--cd-home-faq-question", foregroundCss);
    setPropertyOnNodes(targets.faqs, "--cd-home-faq-answer", mutedCss);
    setPropertyOnNodes(targets.faqs, "--cd-home-faq-rule", lineStrongCss);
    setPropertyOnNodes(targets.faqs, "--cd-home-faq-icon", foregroundCss);
    setInlineColor(targets.faqs, foregroundCss);

    setInlineColor(targets.footers, foregroundCss);
    setInlineColor(targets.primaryText, foregroundCss);
    setInlineColor(targets.mutedText, mutedCss);
    setInlineColor(targets.footerFields, foregroundCss);
    setInlineBorderColor(targets.lineNodes, lineStrongCss);
  }

  function applyColor(color) {
    if (!surface || !color) return;

    var cssValue = colorToCss(color);
    var foreground = getForegroundColor(color);
    var foregroundCss = colorToCss(foreground);
    var foregroundRgb =
      foreground.r + ", " + foreground.g + ", " + foreground.b;
    var hoverCss = "rgba(" + foregroundRgb + ", 0.78)";
    var mutedCss = colorToCss(mixColor(foreground, color, 0.34));
    var invertAmount = getForegroundBlend(color).toFixed(3);
    var lineCss = "rgba(" + foregroundRgb + ", 0.16)";
    var lineStrongCss = "rgba(" + foregroundRgb + ", 0.24)";
    var tbForeground = getBinaryForegroundColor(color);
    var tbForegroundCss = colorToCss(tbForeground);
    var tbForegroundRgb = tbForeground.r + ", " + tbForeground.g + ", " + tbForeground.b;
    var tbMutedCss = "rgba(" + tbForegroundRgb + ", 0.72)";
    var tbLineCss = "rgba(" + tbForegroundRgb + ", 0.18)";
    var tbLineStrongCss = "rgba(" + tbForegroundRgb + ", 0.32)";

    if (surface.dataset.cdPdpSurfaceColor !== cssValue) {
      surface.dataset.cdPdpSurfaceColor = cssValue;
      surface.style.backgroundColor = cssValue;
      document.documentElement.style.setProperty(
        "--cd-pdp-surface-color",
        cssValue,
      );
      if (document.body) {
        document.body.style.setProperty("--cd-pdp-surface-color", cssValue);
      }
    }

    if (surface.dataset.cdPdpSurfaceForeground !== foregroundCss) {
      surface.dataset.cdPdpSurfaceForeground = foregroundCss;
      document.documentElement.style.setProperty(
        "--cd-pdp-surface-fg",
        foregroundCss,
      );
      document.documentElement.style.setProperty(
        "--cd-pdp-surface-fg-rgb",
        foregroundRgb,
      );
      document.documentElement.style.setProperty(
        "--cd-pdp-surface-fg-hover",
        hoverCss,
      );
      document.documentElement.style.setProperty(
        "--cd-pdp-surface-fg-muted",
        mutedCss,
      );
      document.documentElement.style.setProperty(
        "--cd-pdp-surface-logo-invert",
        invertAmount,
      );
      if (document.body) {
        document.body.style.setProperty("--cd-pdp-surface-fg", foregroundCss);
        document.body.style.setProperty("--cd-pdp-surface-fg-rgb", foregroundRgb);
        document.body.style.setProperty("--cd-pdp-surface-fg-hover", hoverCss);
        document.body.style.setProperty("--cd-pdp-surface-fg-muted", mutedCss);
        document.body.style.setProperty("--cd-pdp-surface-logo-invert", invertAmount);
      }
    }

    applyForegroundOverrides(
      cssValue,
      foregroundCss,
      foregroundRgb,
      mutedCss,
      lineCss,
      lineStrongCss,
      invertAmount,
      tbForegroundCss,
      tbMutedCss,
      tbLineCss,
      tbLineStrongCss,
    );
  }

  function update() {
    if (!surface || !stops.length) {
      ticking = false;
      return;
    }

    var anchor = window.scrollY + window.innerHeight * 0.5;
    applyColor(findColor(anchor));
    ticking = false;
  }

  function queueUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  function rebuild() {
    surface = document.getElementById("cd-pdp-surface-canvas");
    if (!surface || !document.body.classList.contains("template-product")) return;

    readStops();

    if (!stops.length) {
      document.documentElement.classList.remove("cd-pdp-surface-ready");
      return;
    }

    document.documentElement.classList.add("cd-pdp-surface-ready");
    measureStops();
    collectTargets();
    queueUpdate();
  }

  document.addEventListener("DOMContentLoaded", rebuild);
  window.addEventListener("load", rebuild);
  window.addEventListener("resize", rebuild);
  window.addEventListener("orientationchange", rebuild);
  window.addEventListener("scroll", queueUpdate, { passive: true });
  document.addEventListener("shopify:section:load", rebuild);
  document.addEventListener("shopify:section:reorder", rebuild);
  document.addEventListener("shopify:section:unload", rebuild);

  window.CDCleerPdpSurfaceBlend = {
    refresh: rebuild,
    update: queueUpdate,
  };

  window.CDCleerPdpHeaderScheme = {
    set: setHeaderSchemeOverride,
    clear: function (source) {
      setHeaderSchemeOverride(source || "default", null);
    },
    update: queueUpdate,
  };
})();
